import { ArrowsClockwise, DownloadSimple } from '@phosphor-icons/react';
import type { FlowItemDto } from '@reelplicate/api-client/model';
import {
  Button,
  IconButton,
  Inline,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Stack,
} from '@reelplicate/ui';

export type RerollWhat = 'image' | 'caption' | 'both' | 'aiImage';

/** A slide or image that opens large: keeps its shape, shows focus. */
const OPEN_BUTTON =
  'relative z-10 block cursor-zoom-in rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-border-focus';

type Slide = {
  text?: string;
  slot?: string;
  /** `images` unset: the image came from the step before (Build carousels). */
  pool?: { images?: string; captions?: string };
  /** An AI background (Carousel slides). */
  image?: { prompt: string };
};

/**
 * What a render node made: a reel's or clip's video with a download link, a
 * carousel's slides in a scrolling strip, or an image. Nothing for text items.
 */
export function ItemMedia({
  item,
  compact = false,
  onOpen,
  onReroll,
  rerolling,
}: {
  item: FlowItemDto;
  compact?: boolean;
  /** Review: open a slide (0-based) or the image large. */
  onOpen?: (slide: number) => void;
  /** Review: swap a slide made from slide pools for another pick, or get a new AI background. */
  onReroll?: (slide: number, what: RerollWhat) => void;
  /** The slide being rerolled. */
  rerolling?: number | null;
}) {
  const { media, payload } = item;
  if (media.length === 0) return null;
  const width = typeof payload.width === 'number' ? payload.width : 9;
  const height = typeof payload.height === 'number' ? payload.height : 16;
  const aspectRatio = `${width} / ${height}`;

  if (item.dataType === 'image') {
    const image = (
      <img
        src={media[0]!.url}
        alt={item.summary}
        style={{ aspectRatio }}
        className={`${compact ? 'h-32' : 'h-44'} bg-bg-subtle w-fit rounded-sm object-cover`}
      />
    );
    return onOpen ? (
      <button
        type="button"
        aria-label={`Open ${item.summary}`}
        onClick={() => onOpen(0)}
        className={OPEN_BUTTON}
      >
        {image}
      </button>
    ) : (
      image
    );
  }

  if (media[0]!.kind === 'image') {
    const slides = Array.isArray(payload.slides) ? (payload.slides as Slide[]) : [];
    return (
      <Inline
        as="ol"
        gap={2}
        aria-label="Slides"
        className="nodrag nowheel overflow-x-auto pb-1"
      >
        {media.map((file, i) => {
          const picture = (
            <img
              src={file.url}
              alt={`Slide ${i + 1}${slides[i]?.text ? `: ${slides[i].text}` : ''}`}
              style={{ aspectRatio }}
              className={`${compact ? 'h-32' : 'h-44'} bg-bg-subtle rounded-sm object-cover ${rerolling === i ? 'opacity-50' : ''}`}
            />
          );
          return (
            <li key={file.url} className="relative shrink-0">
              {onOpen ? (
                // The reroll button is a sibling: buttons can't nest.
                <button
                  type="button"
                  aria-label={`Open slide ${i + 1}`}
                  onClick={() => onOpen(i)}
                  className={OPEN_BUTTON}
                >
                  {picture}
                </button>
              ) : (
                picture
              )}
              {onReroll &&
                slides[i] &&
                (!slides[i].pool || slides[i].pool.images || slides[i].pool.captions) && (
                  <Menu>
                    <MenuTrigger asChild>
                      <IconButton
                        icon={ArrowsClockwise}
                        size="sm"
                        variant="secondary"
                        aria-label={`Reroll slide ${i + 1}${slides[i].slot ? ` (${slides[i].slot})` : ''}`}
                        disabled={rerolling !== null && rerolling !== undefined}
                        className="absolute top-1 right-1 shadow-sm"
                      />
                    </MenuTrigger>
                    <MenuContent align="end">
                      {!slides[i].pool && (
                        <MenuItem onSelect={() => onReroll(i, 'aiImage')}>
                          {slides[i].image ? 'New AI image' : 'Add an AI image'}
                        </MenuItem>
                      )}
                      {slides[i].pool?.images && (
                        <MenuItem onSelect={() => onReroll(i, 'image')}>
                          New image
                        </MenuItem>
                      )}
                      {slides[i].pool?.captions && (
                        <MenuItem onSelect={() => onReroll(i, 'caption')}>
                          New caption
                        </MenuItem>
                      )}
                      {slides[i].pool?.images && slides[i].pool?.captions && (
                        <MenuItem onSelect={() => onReroll(i, 'both')}>
                          New image and caption
                        </MenuItem>
                      )}
                    </MenuContent>
                  </Menu>
                )}
            </li>
          );
        })}
      </Inline>
    );
  }

  const video = media[0]!;
  return (
    <Stack gap={1}>
      <video
        src={video.url}
        aria-label={`Video: ${item.summary}`}
        controls
        playsInline
        preload="metadata"
        style={{ aspectRatio }}
        className={`${compact ? 'max-h-64' : 'max-h-80'} w-full rounded-sm bg-black object-contain`}
      />
      <Button
        asChild
        variant="ghost"
        size="sm"
        leadingIcon={DownloadSimple}
        className="self-start"
      >
        <a href={video.url} download>
          Download
        </a>
      </Button>
    </Stack>
  );
}
